import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/solutions", label: "Solutions" },
  { to: "/professional-services", label: "Professional Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/industries", label: "Industries" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "border-white/10 bg-ink/85 backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" aria-label="Synergy Outsourcing home">
          <img
            src="/logo-full.png"
            alt="Synergy Outsourcing"
            className="h-[60px] w-auto sm:h-[66px]"
          />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium transition-colors ${
                  isActive ? "text-brand" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <Link
            to="/contact"
            data-testid="nav-talk-to-us"
            className="rounded-full border border-white/15 px-4 py-2 text-[13px] font-semibold text-slate-200 transition-colors hover:border-brand/50 hover:text-white"
          >
            Talk to Us
          </Link>
          <Link
            to="/get-your-team"
            data-testid="nav-build-your-team"
            className="group flex items-center gap-1.5 rounded-full bg-brand px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:bg-brand-bright"
          >
            Build Your Team
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          onClick={() => setOpen((v) => !v)}
          className="rounded-lg p-2 text-slate-200 xl:hidden"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden border-b border-white/10 bg-ink/95 backdrop-blur-xl xl:hidden"
            aria-label="Mobile"
          >
            <div className="space-y-1 px-4 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `block rounded-lg px-3 py-2.5 text-sm font-medium ${
                      isActive ? "bg-white/5 text-brand" : "text-slate-300 hover:bg-white/5"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex gap-3 pt-3">
                <Link
                  to="/contact"
                  data-testid="mobile-talk-to-us"
                  className="flex-1 rounded-full border border-white/15 px-4 py-2.5 text-center text-sm font-semibold text-slate-200"
                >
                  Talk to Us
                </Link>
                <Link
                  to="/get-your-team"
                  data-testid="mobile-build-your-team"
                  className="flex-1 rounded-full bg-brand px-4 py-2.5 text-center text-sm font-semibold text-ink"
                >
                  Build Your Team
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
